// Dashboard section: monthly reservation stats by status as a vertical column
// chart, with a compact potential-fees and month-earnings summary.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Flex, useToken } from "@chakra-ui/react";
import { MdPayments } from "react-icons/md";
import CardHeader from "../../shared/CardHeader";
import ColumnBars from "../../shared/ColumnBars";
import CompactFees from "../../shared/CompactFees";

/** Status columns with their stats key, short caption and Chakra color token. */
const STATUSES = [
  { label: "Payés", key: "paid", token: "brand.400" },
  { label: "Réal.", key: "realized", token: "green.500" },
  { label: "Accep.", key: "accepted", token: "blue.500" },
  { label: "Att.", key: "pending", token: "orange.500" },
  { label: "Ref.", key: "declined", token: "red.500" }
];

const EMPTY = { paid: 0, pending: 0, accepted: 0, declined: 0, realized: 0 };

/**
 * Displays the current month's reservation stats as vertical columns plus a
 * compact fees summary, filling the section height.
 * @returns {React.ReactElement}
 */
export default function EventsSection() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(EMPTY);
  const [potentialFees, setPotentialFees] = useState([]);
  const [monthFees, setMonthFees] = useState([]);
  const colors = useToken("colors", STATUSES.map((s) => s.token));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/admin/reservation/income_month");
      setStats({
        paid: data.reservation_paid, realized: data.reservation_finished_number_month,
        accepted: data.reservation_accepted, pending: data.reservation_pending,
        declined: data.reservation_declined
      });
      setPotentialFees(data.potential_fees || []);
      setMonthFees(data.month_fees || []);
    } catch (error) {
      console.error("Failed to fetch income stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = STATUSES.map((s, i) => ({ label: s.label, value: stats[s.key], color: colors[i] }));
  return (
    <Flex direction="column" h="100%">
      <CardHeader icon={MdPayments} title="Événements du mois" onRefresh={load} loading={loading} />
      <ColumnBars items={items} />
      <CompactFees potential={potentialFees} month={monthFees} />
    </Flex>
  );
}
